// src/index.ts
function shadeColour(hex, percent) {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) hex = hex.split("").map((x) => x + x).join("");
  const num = parseInt(hex, 16);
  let r = num >> 16 & 255;
  let g = num >> 8 & 255;
  let b = num & 255;
  r = Math.min(255, Math.max(0, Math.round(r + percent * 255)));
  g = Math.min(255, Math.max(0, Math.round(g + percent * 255)));
  b = Math.min(255, Math.max(0, Math.round(b + percent * 255)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
function mountVisualisation(options = {}) {
  const externalLength = options.externalLength ?? 297;
  const externalWidth = options.externalWidth ?? 210;
  const apertureLength = options.apertureLength ?? 210;
  const apertureWidth = options.apertureWidth ?? 148;
  const apertureOffsetTop = options.apertureOffsetTop ?? (externalLength - apertureLength) / 2;
  const apertureOffsetLeft = options.apertureOffsetLeft ?? (externalWidth - apertureWidth) / 2;
  const boardThickness = options.boardThickness ?? 1.4;
  const mountColour = options.mountColour ?? "#e0e0e0";
  const bevelColour = options.bevelColour ?? "#eee";
  const apertureShape = (options.apertureShape ?? "rectangle").toLowerCase() === "oval" || (options.apertureShape ?? "").toLowerCase() === "circle" ? "oval" : "rectangle";
  const maxPx = 3e3;
  const longSide = Math.max(externalLength, externalWidth);
  let scale;
  if (options.scale !== void 0) {
    scale = options.scale;
  } else {
    scale = 600 / longSide;
  }
  if (scale * longSide > maxPx) {
    scale = maxPx / longSide;
  }
  const pxWidth = Math.round(externalWidth * scale);
  const pxHeight = Math.round(externalLength * scale);
  const canvas = document.createElement("canvas");
  canvas.style.display = "block";
  canvas.style.maxWidth = "100%";
  canvas.style.maxHeight = "100%";
  canvas.width = pxWidth;
  canvas.height = pxHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  ctx.clearRect(0, 0, pxWidth, pxHeight);
  ctx.fillStyle = mountColour;
  ctx.fillRect(0, 0, pxWidth, pxHeight);
  const aperturePxW = apertureWidth * scale;
  const aperturePxH = apertureLength * scale;
  const apertureX = apertureOffsetLeft * scale;
  const apertureY = apertureOffsetTop * scale;
  const bevelWidth = boardThickness * scale;
  ctx.save();
  ctx.fillStyle = bevelColour;
  if (apertureShape === "oval") {
    const avgCircumference = Math.PI * (aperturePxW + aperturePxH) / 2;
    const segments = Math.max(24, Math.min(128, Math.round(avgCircumference / 8)));
    const cx = apertureX + aperturePxW / 2;
    const cy = apertureY + aperturePxH / 2;
    const rx = aperturePxW / 2;
    const ry = aperturePxH / 2;
    const rxo = rx + bevelWidth;
    const ryo = ry + bevelWidth;
    const baseColour = bevelColour;
    const lightAngle = 45 * Math.PI / 180;
    const epsilon = 0.02;
    const insetPx = 1;
    const rxInset = Math.max(0, rx - insetPx / scale);
    const ryInset = Math.max(0, ry - insetPx / scale);
    for (let i = 0; i < segments; ++i) {
      const theta0 = i / segments * 2 * Math.PI - epsilon;
      const theta1 = (i + 1) / segments * 2 * Math.PI + epsilon;
      const midAngle = (i + 0.5) / segments * 2 * Math.PI;
      const dot = Math.cos(midAngle - lightAngle);
      const shade = dot < 0 ? -0.12 * -dot : 0;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rxo, ryo, 0, theta0, theta1);
      ctx.ellipse(cx, cy, rxInset, ryInset, 0, theta1, theta0, true);
      ctx.closePath();
      ctx.fillStyle = shadeColour(baseColour, shade);
      ctx.fill();
    }
  } else {
    const baseColour = bevelColour;
    const tl = { x: apertureX, y: apertureY };
    const tr = { x: apertureX + aperturePxW, y: apertureY };
    const br = { x: apertureX + aperturePxW, y: apertureY + aperturePxH };
    const bl = { x: apertureX, y: apertureY + aperturePxH };
    const offset = bevelWidth / Math.SQRT2;
    const otl = { x: tl.x - offset, y: tl.y - offset };
    const otr = { x: tr.x + offset, y: tr.y - offset };
    const obr = { x: br.x + offset, y: br.y + offset };
    const obl = { x: bl.x - offset, y: bl.y + offset };
    ctx.beginPath();
    ctx.moveTo(otl.x, otl.y);
    ctx.lineTo(otr.x, otr.y);
    ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(tl.x, tl.y);
    ctx.closePath();
    ctx.fillStyle = shadeColour(baseColour, -0.12);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(otr.x, otr.y);
    ctx.lineTo(obr.x, obr.y);
    ctx.lineTo(br.x, br.y);
    ctx.lineTo(tr.x, tr.y);
    ctx.closePath();
    ctx.fillStyle = baseColour;
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(obr.x, obr.y);
    ctx.lineTo(obl.x, obl.y);
    ctx.lineTo(bl.x, bl.y);
    ctx.lineTo(br.x, br.y);
    ctx.closePath();
    ctx.fillStyle = baseColour;
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(obl.x, obl.y);
    ctx.lineTo(otl.x, otl.y);
    ctx.lineTo(tl.x, tl.y);
    ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
    ctx.fillStyle = shadeColour(baseColour, -0.12);
    ctx.fill();
  }
  ctx.restore();
  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  if (apertureShape === "oval") {
    ctx.beginPath();
    ctx.ellipse(
      apertureX + aperturePxW / 2,
      apertureY + aperturePxH / 2,
      aperturePxW / 2,
      aperturePxH / 2,
      0,
      0,
      2 * Math.PI
    );
    ctx.fill();
  } else {
    ctx.fillRect(apertureX, apertureY, aperturePxW, aperturePxH);
  }
  ctx.restore();
  return canvas;
}
var MountVisualisationElement = void 0;
if (typeof HTMLElement !== "undefined") {
  MountVisualisationElement = class extends HTMLElement {
    canvas;
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.canvas = document.createElement("canvas");
      this.shadowRoot?.appendChild(this.canvas);
    }
    static get observedAttributes() {
      return [
        "external-length",
        "external-width",
        "aperture-length",
        "aperture-width",
        "scale",
        "mount-colour",
        "bevel-colour",
        "board-thickness",
        "aperture-shape",
        "aperture-offset-top",
        "aperture-offset-left"
      ];
    }
    connectedCallback() {
      this.renderVisualisation();
    }
    attributeChangedCallback() {
      this.renderVisualisation();
    }
    getOptions() {
      const getNumberAttr = (name, fallback) => {
        const val = this.getAttribute(name);
        if (val === null || val === "") return fallback;
        const num = parseFloat(val);
        return isNaN(num) ? fallback : num;
      };
      return {
        externalLength: getNumberAttr("external-length", 297),
        externalWidth: getNumberAttr("external-width", 210),
        apertureLength: getNumberAttr("aperture-length", 210),
        apertureWidth: getNumberAttr("aperture-width", 148),
        boardThickness: getNumberAttr("board-thickness", 1.4),
        mountColour: this.getAttribute("mount-colour") || void 0,
        bevelColour: this.getAttribute("bevel-colour") || void 0,
        apertureShape: this.getAttribute("aperture-shape") || void 0,
        scale: this.hasAttribute("scale") ? getNumberAttr("scale") : void 0,
        apertureOffsetTop: getNumberAttr("aperture-offset-top"),
        apertureOffsetLeft: getNumberAttr("aperture-offset-left")
      };
    }
    renderVisualisation() {
      const options = this.getOptions();
      const newCanvas = mountVisualisation(options);
      if (this.canvas.parentNode) {
        this.shadowRoot?.replaceChild(newCanvas, this.canvas);
      }
      this.canvas = newCanvas;
    }
  };
}
function createElements() {
  if (typeof window !== "undefined" && typeof MountVisualisationElement !== "undefined" && window.customElements && !window.customElements.get("mount-visualisation")) {
    window.customElements.define("mount-visualisation", MountVisualisationElement);
  }
}
if (typeof window !== "undefined" && typeof document !== "undefined" && typeof document.currentScript !== "undefined" && !(typeof import.meta !== "undefined" && import.meta.url)) {
  createElements();
}
export {
  createElements,
  mountVisualisation
};
